import { useTranslate } from 'react-admin';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import type { AnchorType } from './types';

interface EntityPickerProps {
  anchorType: AnchorType;
  anchorId: string;
  onChange: (next: { anchorType: AnchorType; anchorId: string }) => void;
  disabled?: boolean;
}

const ANCHOR_TYPES: AnchorType[] = [
  'none',
  'charger',
  'session',
  'user',
  'transaction',
  'reservation',
];

// Anchor Select (none | charger | session | user | transaction | reservation) + an anchorId
// free-text TextField (required when anchorType !== 'none'). Phase 1: plain id entry — an
// Autocomplete-backed per-type lookup is a deferred follow-up (sub-plan 14 Edge cases).
export const EntityPicker = ({ anchorType, anchorId, onChange, disabled }: EntityPickerProps) => {
  const translate = useTranslate();
  const requireId = anchorType !== 'none';
  const idMissing = requireId && anchorId.trim() === '';

  const handleTypeChange = (e: SelectChangeEvent) => {
    const next = e.target.value as AnchorType;
    // Clear the id when switching back to 'none' so a stale id is not submitted.
    onChange({ anchorType: next, anchorId: next === 'none' ? '' : anchorId });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <FormControl fullWidth disabled={disabled}>
          <InputLabel id="ai-anchor-type-label">
            {translate('aiTroubleshooting.form.anchorType')}
          </InputLabel>
          <Select
            labelId="ai-anchor-type-label"
            value={anchorType}
            label={translate('aiTroubleshooting.form.anchorType')}
            onChange={handleTypeChange}
          >
            {ANCHOR_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {translate(`aiTroubleshooting.anchorType.${t}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ flex: 1 }}>
        <TextField
          fullWidth
          label={translate('aiTroubleshooting.form.anchorId')}
          value={anchorId}
          onChange={(e) => onChange({ anchorType, anchorId: e.target.value })}
          disabled={disabled || anchorType === 'none'}
          required={requireId}
          error={idMissing}
          helperText={
            anchorType === 'none'
              ? translate('aiTroubleshooting.form.anchorIdHelperNone')
              : idMissing
                ? translate('aiTroubleshooting.form.anchorIdRequired')
                : translate('aiTroubleshooting.form.anchorIdHelper')
          }
        />
      </Box>
    </Box>
  );
};

export default EntityPicker;
